import * as Sentry from '@sentry/browser';
import BaseInterceptor from '@/router/interceptors/BaseInterceptor.js';
import sessionMonitor from '@/services/SessionMonitor.js';
import { getPendingNavigationFullPath } from '@/utilities/accessDeniedLocation.js';
import { resolveSessionExpiryReturnPath } from '@/utilities/sessionExpiryReturnPath.js';

class UnauthenticatedInterceptor extends BaseInterceptor {
    async handle(status) {
        const currentPath = this.$router.currentRoute.value.path;

        if (status === 401) {
            // Concurrent 401s after the first expiry — already handled
            if (sessionMonitor.isSessionTimedOut() || sessionMonitor.isRedirectInProgress()) {
                return true;
            }

            // Expected when the user was never authenticated this session
            if (!this.$store.getters['Auth/check']) {
                if (sessionMonitor.isAuthEntryPath(currentPath)) {
                    sessionMonitor.reset();
                }

                return true;
            }

            // Authenticated 401 is expiry even on `/` (home dashboard). Do not
            // key this off currentRoute: a destination fetch can 401 before the
            // router confirms the new page.
            const currentFullPath = this.$router.currentRoute.value.fullPath || currentPath;
            const intendedPath = resolveSessionExpiryReturnPath({
                pendingFullPath: getPendingNavigationFullPath(),
                rememberedFullPath: sessionMonitor.getIntendedPath(),
                locationFull: currentFullPath,
                isAuthEntryPath: path => sessionMonitor.isAuthEntryPath(path),
            });

            if (intendedPath) {
                sessionMonitor.rememberNavigation(intendedPath);
                this.$store.dispatch('Auth/saveReturnPath', intendedPath.split('?')[0]);
            }

            try {
                sessionMonitor.handleSessionTimeout();
            } catch (error) {
                console.error('SessionMonitor.handleSessionTimeout() threw an error:', error);

                Sentry.captureException(error, {
                    tags: {
                        error_type: 'session_timeout_handler_failed',
                        component: 'UnauthenticatedInterceptor',
                    },
                    contexts: {
                        session: {
                            current_path: currentPath,
                            intended_path: intendedPath,
                            session_monitor_available: true,
                        },
                    },
                });
            }

            this.$store.dispatch('Auth/setSessionTimedOut', true);

            return true;
        }

        if (sessionMonitor.isAuthEntryPath(currentPath)) {
            sessionMonitor.reset();
            return true;
        }

        return false;
    }
}

export default UnauthenticatedInterceptor;
